// src/components/modals/CourseSyllabusModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import SpinnerOverlay from '../ui/SpinnerOverlay';
import FileUploadArea from '../ui/FileUploadArea';

/* ── Icons ───────────────────────────────────────────────────── */
const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

/* ── Main component ──────────────────────────────────────────── */
const CourseSyllabusModal = ({ isOpen, onClose, courseTitle, onSubmit, initialUrl = '' }) => {
  const [selectedFile,  setSelectedFile]  = useState(null);
  const [fileInputKey,  setFileInputKey]  = useState(0);
  const [previewUrl,    setPreviewUrl]    = useState(initialUrl || '');
  const [uploading,     setUploading]     = useState(false);
  const [error,         setError]         = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const modalRef = useRef(null);

  /* reset when modal opens */
  useEffect(() => {
    if (!isOpen) return;
    setSelectedFile(null);
    setFileInputKey(k => k + 1);
    setPreviewUrl(initialUrl || '');
    setError('');
    setUploadSuccess(false);
  }, [initialUrl, isOpen]);

  /* local file preview */
  useEffect(() => {
    if (!selectedFile) { setPreviewUrl(prev => (initialUrl && !prev ? initialUrl : prev)); return; }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile, initialUrl]);

  /* scroll lock + ESC */
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileChange = (file) => { setSelectedFile(file); setError(''); setUploadSuccess(false); };
  const handleRemoveFile = () => {
    setSelectedFile(null); setError(''); setUploadSuccess(false);
    setFileInputKey(k => k + 1); setPreviewUrl(initialUrl || '');
  };
  const handleClose = () => {
    setError(''); setUploadSuccess(false); setSelectedFile(null); setFileInputKey(k => k + 1); onClose?.();
  };

  const handleUploadFile = async () => {
    if (!selectedFile) { setError('נא לבחור קובץ PDF לפני השמירה'); return; }
    try {
      setUploading(true); setError('');
      const safeName = selectedFile.name.replace(/\s+/g, '_');
      const fileRef = ref(storage, `course-syllabi/${Date.now()}-${safeName}`);
      await uploadBytes(fileRef, selectedFile);
      const downloadURL = await getDownloadURL(fileRef);
      await onSubmit?.(downloadURL);
      setUploadSuccess(true);
      setTimeout(() => handleClose(), 1500);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'אירעה שגיאה בהעלאת הקובץ ושמירת הסילבוס');
    } finally {
      setUploading(false);
    }
  };

  const hasExisting = !!initialUrl;

  const content = (
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { if (!modalRef.current?.contains(e.target)) handleClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" onClick={handleClose}/>

      {/* Modal */}
      <div
        ref={modalRef}
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
        className="relative z-[1] w-full sm:max-w-2xl bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20
                   flex flex-col max-h-[96dvh] sm:max-h-[90vh] overflow-hidden"
      >
        {uploading && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

        {/* ── Header ── */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z"/>
                <polyline points="14 2 14 9 21 9"/>
                <line x1="9" y1="13" x2="15" y2="13"/>
                <line x1="9" y1="17" x2="15" y2="17"/>
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight">סילבוס הקורס</h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">ניהול והעלאת סילבוס עבור {courseTitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="סגור"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 space-y-4">

          {/* Info banner */}
          <div className="flex items-start gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 px-4 py-3.5">
            <svg className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
            </svg>
            <p className="text-xs text-indigo-700 leading-relaxed">
              {hasExisting
                ? 'ניתן לצפות בסילבוס הנוכחי או להעלות קובץ חדש שיחליף אותו.'
                : 'העלה קובץ PDF כדי להוסיף סילבוס לקורס.'}
            </p>
          </div>

          {/* Upload area */}
          <FileUploadArea
            selectedFile={selectedFile}
            fileInputKey={fileInputKey}
            onFileChange={handleFileChange}
            onRemoveFile={handleRemoveFile}
            onUpload={handleUploadFile}
            customStyles={{
              container:
                'rounded-2xl border-2 border-dashed border-gray-200 hover:border-indigo-300 transition-all duration-200 bg-gray-50/60 p-5',
              uploadButton:
                'inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold shadow-md shadow-indigo-200 hover:shadow-lg hover:shadow-indigo-300 hover:-translate-y-px transition-all duration-150',
            }}
          />

          {/* PDF preview */}
          {previewUrl && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  <DownloadIcon/> פתח בחלון חדש
                </a>
                <span className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase tracking-wide">
                  <EyeIcon/> תצוגה מקדימה
                </span>
              </div>
              <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                <iframe
                  src={previewUrl}
                  title="PDF preview"
                  className="w-full h-72 sm:h-80 bg-white"
                  style={{ border: 'none' }}
                />
              </div>
            </div>
          )}

          {/* Success */}
          {uploadSuccess && (
            <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-800">הסילבוס הועלה בהצלחה!</p>
                <p className="text-xs text-emerald-600 mt-0.5">המסמך נשמר במערכת ומוכן לצפייה.</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <p className="text-sm text-red-700 leading-relaxed">{error}</p>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={uploading}
            className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500
                       hover:bg-gray-50 hover:border-gray-300 transition-all duration-150
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            סגור
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
};

export default CourseSyllabusModal;